import React from 'react';
import { Helmet } from 'react-helmet-async';
import Layout from '@/components/Layout/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { 
  HelpCircle, 
  Phone, 
  Calendar, 
  DollarSign, 
  Clock, 
  Shield,
  Home,
  Wrench,
  FileText,
  MapPin,
  CloudRain,
  Hammer,
  Building,
  HardHat,
  Banknote
} from 'lucide-react';

const FAQPage = () => {
  const faqCategories = [
    {
      title: "General Roofing Questions",
      icon: Home,
      questions: [
        {
          question: "How do I know if I need a new roof?",
          answer: "Signs you may need a new roof include: shingles that are curling, cracking, or missing; granules in gutters; leaks or water stains; sagging roof deck; or if your roof is over 20-25 years old. We offer free inspections to assess your roof's condition. For comprehensive guidelines, the National Roofing Contractors Association provides detailed homeowner resources."
        },
        {
          question: "How long does a typical roof last?",
          answer: "According to industry standards from the Asphalt Roofing Manufacturers Association, asphalt shingles typically last 20-30 years, metal roofs 40-70 years, and tile roofs can last 50+ years. Lifespan depends on material quality, installation, maintenance, and weather conditions in Eastern NC."
        },
        {
          question: "What's the best roofing material for North Carolina weather?",
          answer: "For Eastern NC's climate, we recommend architectural asphalt shingles, metal roofing, or synthetic materials based on North Carolina State University climate studies. These handle our humidity, storms, and temperature changes well. The U.S. Department of Energy also provides guidance on cool roofing options for energy efficiency."
        },
        {
          question: "Should I repair or replace my roof?",
          answer: "If damage is localized and your roof is under 15 years old, repairs may be sufficient. If you have widespread damage, multiple leaks, or an aging roof (20+ years), replacement is usually more cost-effective long-term. Consumer Reports provides detailed guidance on making this decision."
        }
      ]
    },
    {
      title: "Installation & Process",
      icon: Wrench,
      questions: [
        {
          question: "How long does a roof replacement take?",
          answer: "Most residential roof replacements take 1-3 days, depending on size, complexity, and weather. Larger or more complex roofs may take longer. We'll provide a specific timeline during your estimate."
        },
        {
          question: "What happens if it rains during installation?",
          answer: "We monitor weather closely using National Weather Service forecasts and use tarps and protective measures. If severe weather is predicted, we'll pause work and secure your roof. We never compromise on protecting your home from water damage."
        },
        {
          question: "Do I need to be home during installation?",
          answer: "You don't need to be home, but we recommend someone be available for questions and final walkthrough. We'll coordinate with you on timing and access needs."
        },
        {
          question: "How do you protect my landscaping and property?",
          answer: "We use tarps, plywood, and magnetic tools to protect landscaping, vehicles, and outdoor furniture. Our crew performs thorough cleanup daily, including magnet sweeps for nails and debris."
        }
      ]
    },
    {
      title: "Cost & Financing",
      icon: DollarSign,
      questions: [
        {
          question: "How much does a new roof cost?",
          answer: "Roof replacement costs vary based on size, materials, complexity, and local factors. In Eastern NC, typical ranges are $8,000-$25,000. We provide detailed, transparent estimates after our free inspection."
        },
        {
          question: "Do you offer financing options?",
          answer: "Yes! We offer multiple financing options including competitive rate personal loans and flexible payment terms. Most applications are approved quickly, often same-day."
        },
        {
          question: "What's included in your estimates?",
          answer: "Our estimates include all materials, labor, permits, disposal, cleanup, and warranty coverage. We provide detailed breakdowns with no hidden fees or surprise charges."
        },
        {
          question: "Do you provide free estimates?",
          answer: "Yes, we provide completely free, no-obligation estimates including thorough roof inspections. We'll assess your roof condition and provide detailed written recommendations."
        }
      ]
    },
    {
      title: "Insurance & Storm Damage",
      icon: Shield,
      questions: [
        {
          question: "Will insurance cover my roof replacement?",
          answer: "Insurance typically covers storm damage, sudden events, and qualifying wear issues according to Insurance Information Institute guidelines. We work directly with insurance companies, provide detailed documentation following FEMA standards, and help navigate the claims process."
        },
        {
          question: "How do I file an insurance claim for roof damage?",
          answer: "Contact your insurance company first, then call us. We'll document damage using professional methods, provide detailed reports meeting industry standards, meet with adjusters, and advocate for proper coverage of necessary repairs or replacement."
        },
        {
          question: "What if my insurance claim is denied?",
          answer: "We help appeal denied claims by providing additional documentation, expert assessments, and working with public adjusters when needed. Many initially denied claims are successfully appealed."
        },
        {
          question: "Do you work with all insurance companies?",
          answer: "Yes, we work with all major insurance providers in North Carolina. We understand their requirements and processes, making claims smoother for homeowners."
        }
      ]
    },
    {
      title: "Warranties & Service",
      icon: FileText,
      questions: [
        {
          question: "What warranties do you provide?",
          answer: "We provide comprehensive workmanship warranties starting at 5 years (up to 10 years on certain packages) plus manufacturer material warranties (20-50+ years depending on material). Certain warranty packages are transferable to new homeowners."
        },
        {
          question: "Do you provide emergency roof services?",
          answer: "Yes, we offer 24/7 emergency services for storm damage, leaks, and urgent repairs. We'll secure your property immediately and schedule permanent repairs quickly."
        },
        {
          question: "How do I maintain my new roof?",
          answer: "Regular maintenance includes: annual inspections, gutter cleaning, removing debris, checking flashing, and addressing minor issues promptly. The Asphalt Roofing Manufacturers Association provides comprehensive maintenance guidelines. We provide maintenance checklists and ongoing support based on industry best practices."
        },
        {
          question: "What areas do you serve?",
          answer: "We serve all of Eastern North Carolina including Rocky Mount, Greenville, Wilson, Tarboro, Zebulon, Raleigh area, and surrounding communities. Contact us to confirm service in your specific location."
        }
      ]
    },
    // New comprehensive FAQ categories
    {
      title: "Climate & Maintenance",
      icon: CloudRain,
      questions: [
        {
          question: "How often should I have my roof inspected in North Carolina?",
          answer: "At least twice a year — in spring and fall — plus anytime after a major storm."
        },
        {
          question: "How does hurricane season affect roofs in NC?",
          answer: "High winds and flying debris are leading causes of shingle loss and leaks."
        },
        {
          question: "Does coastal salt air damage roofing materials?",
          answer: "Yes. Salt accelerates corrosion of metal roofs and shortens shingle life."
        },
        {
          question: "How does humidity impact shingles in NC?",
          answer: "It promotes algae, moss, and mold growth, which weaken roofing materials."
        },
        {
          question: "What type of maintenance prevents storm damage?",
          answer: "Professional inspections, gutter cleaning, attic ventilation checks, and trimming nearby trees."
        },
        {
          question: "How do falling pine needles or leaves affect roofs?",
          answer: "They trap moisture, leading to rot and moss growth."
        },
        {
          question: "Can algae streaks on shingles cause leaks?",
          answer: "Yes — they eat away at protective granules, shortening roof life."
        },
        {
          question: "Should I pressure wash my roof?",
          answer: "No. Pressure washing strips shingle granules. Carolina Trophy Roofs recommends soft-wash cleaning."
        },
        {
          question: "What attic ventilation issues damage NC roofs?",
          answer: "Poor airflow leads to condensation, mold, and premature shingle curling."
        },
        {
          question: "How do I storm-proof my roof before hurricane season?",
          answer: "Schedule a professional inspection, secure loose shingles, and reinforce flashing."
        },
        {
          question: "Does hail damage always show visible signs?",
          answer: "No. Small dents or bruising may not be visible from the ground but still void warranties."
        },
        {
          question: "How do snow and ice affect NC roofs?",
          answer: "Ice dams can form in colder areas, forcing water under shingles."
        },
        {
          question: "Can humidity in NC cause roof deck rot?",
          answer: "Yes. Moisture buildup under shingles weakens decking."
        },
        {
          question: "How long do gutters last in NC's climate?",
          answer: "15–20 years with regular cleaning. Clogged gutters shorten roof life."
        },
        {
          question: "What's the most common roofing issue in NC neighborhoods?",
          answer: "Wind-lifted shingles during thunderstorms and hurricanes."
        }
      ]
    },
    {
      title: "Roof Repair & Replacement",
      icon: Hammer,
      questions: [
        {
          question: "How do I know if I need a roof repair or replacement?",
          answer: "Localized leaks may be repaired, but widespread issues or older roofs usually require replacement."
        },
        {
          question: "What's the average lifespan of an asphalt roof in NC?",
          answer: "15–20 years, shorter in coastal or high-humidity regions."
        },
        {
          question: "How long does a roof replacement take?",
          answer: "Most projects take 2–5 days, depending on size, pitch, and weather."
        },
        {
          question: "Can I reroof over old shingles?",
          answer: "It's allowed once, but not recommended. We prefer complete tear-offs to inspect decking."
        },
        {
          question: "What happens if I ignore minor roof leaks?",
          answer: "Leaks spread quickly, leading to rot, mold, and costly structural issues."
        },
        {
          question: "What signs mean my roof is failing?",
          answer: "Missing shingles, curled edges, water stains, or sagging attic boards."
        },
        {
          question: "Does a roof replacement require me to leave my home?",
          answer: "No. You can usually stay home, though it will be noisy."
        },
        {
          question: "Can storm damage be repaired without full replacement?",
          answer: "Yes, if damage is localized. But insurers may approve replacement for widespread impact."
        },
        {
          question: "Do roof repairs come with warranties?",
          answer: "Yes — Carolina Trophy Roofs provides written repair warranties."
        },
        {
          question: "How often do flashing systems need replacement?",
          answer: "They should be inspected annually and replaced during reroofing."
        },
        {
          question: "Can skylights cause roof leaks?",
          answer: "Yes, if flashing fails. We reseal skylights during replacements."
        },
        {
          question: "Should chimneys be resealed during roof work?",
          answer: "Yes. Chimney flashing is a common leak point."
        },
        {
          question: "Do clogged gutters damage roofs?",
          answer: "Yes, by forcing water under shingles."
        },
        {
          question: "Can DIY roof repairs save money?",
          answer: "Not long term. Most DIY patches fail, costing more later."
        },
        {
          question: "How do I extend my roof's lifespan in NC?",
          answer: "Routine maintenance, algae-resistant shingles, and annual inspections."
        }
      ]
    },
    {
      title: "Roofing Materials & Options",
      icon: Building,
      questions: [
        {
          question: "What are the most common roofing materials in NC?",
          answer: "Architectural shingles and metal roofing are most popular."
        },
        {
          question: "Are impact-resistant shingles worth it in NC?",
          answer: "Yes. They resist hail and may lower insurance rates."
        },
        {
          question: "How long does metal roofing last in NC?",
          answer: "40–70 years depending on gauge and installation quality."
        },
        {
          question: "Do metal roofs handle hurricanes better than shingles?",
          answer: "Yes. Standing seam metal withstands high winds."
        },
        {
          question: "Are tile or slate roofs common in NC?",
          answer: "Less common due to cost and weight, but highly durable."
        },
        {
          question: "Which roofing materials reduce energy costs?",
          answer: "Metal and reflective shingles reduce cooling bills."
        },
        {
          question: "Do light-colored roofs keep houses cooler?",
          answer: "Yes. Reflective shingles lower summer heat."
        },
        {
          question: "Are architectural shingles better than 3-tab shingles?",
          answer: "Yes. They're stronger, more durable, and carry longer warranties."
        },
        {
          question: "Do green or living roofs work in NC?",
          answer: "Rare, but possible with proper waterproofing."
        },
        {
          question: "Can solar panels damage roofs?",
          answer: "Not if installed properly. We prep roofs for future solar installations."
        },
        {
          question: "Are algae-resistant shingles worth it?",
          answer: "Yes. They extend lifespan in humid NC climates."
        },
        {
          question: "Do flat roofs require special materials?",
          answer: "Yes. TPO, EPDM, and PVC membranes are best."
        },
        {
          question: "What underlayment is best for storm-prone areas?",
          answer: "Peel-and-stick or synthetic underlayments resist water intrusion."
        },
        {
          question: "What's the difference between ridge vents and box vents?",
          answer: "Ridge vents provide continuous airflow, better for humid climates."
        },
        {
          question: "Do all manufacturers offer hurricane-rated shingles?",
          answer: "Yes, but wind ratings vary. We install shingles tested to 130 mph."
        }
      ]
    },
    {
      title: "Hiring a Contractor",
      icon: HardHat,
      questions: [
        {
          question: "How do I choose the right roofer in NC?",
          answer: "Work with established local companies, check reviews, and ask for references."
        },
        {
          question: "What are \"storm chaser\" roofers?",
          answer: "Out-of-town contractors who appear after storms and often do poor work."
        },
        {
          question: "What questions should I ask a roofer?",
          answer: "Ask about materials, warranties, crews, and insurance."
        },
        {
          question: "Should I get multiple roofing estimates?",
          answer: "Yes — compare at least three written estimates."
        },
        {
          question: "Why are some bids much lower than others?",
          answer: "Cheap bids often cut corners on labor or materials."
        },
        {
          question: "Should I pay a roofer upfront?",
          answer: "Carolina Trophy Roofs requires a deposit of 1/3 to 1/2, depending on project size, with balance due after work is complete. Avoid contractors who demand 100% upfront."
        },
        {
          question: "How do I check a roofer's reputation?",
          answer: "Look for local reviews, BBB rating, and community references."
        },
        {
          question: "Do roofers offer financing?",
          answer: "Yes. Carolina Trophy Roofs provides flexible financing options."
        },
        {
          question: "Do roofers need insurance?",
          answer: "Yes. We carry liability and workers' comp for your protection."
        },
        {
          question: "How do I spot roofing scams in NC?",
          answer: "High-pressure sales, no written contract, and large upfront payments."
        },
        {
          question: "What does \"certified installer\" mean?",
          answer: "It means training and approval from manufacturers like GAF or Owens Corning."
        },
        {
          question: "Can I negotiate a roofing estimate?",
          answer: "Yes, but focus on scope and quality, not just price."
        },
        {
          question: "Do roofers clean up after replacement?",
          answer: "Yes. Carolina Trophy Roofs provides full cleanup and magnet sweeps."
        },
        {
          question: "Can I stay home during roofing?",
          answer: "Yes. It will be noisy, but safe."
        },
        {
          question: "Do roofers provide written contracts?",
          answer: "Yes. Always demand a written scope and warranty."
        }
      ]
    },
    {
      title: "Costs, ROI & Financing",
      icon: Banknote,
      questions: [
        {
          question: "What factors drive roof replacement costs in NC?",
          answer: "Size, pitch, materials, labor, hidden issues, and timing."
        },
        {
          question: "Is it cheaper to repair or replace a roof?",
          answer: "Repairs cost less now, but replacement is cheaper long term if damage is widespread."
        },
        {
          question: "Does delaying roof repairs increase costs?",
          answer: "Yes — interior damage, mold, and insurance denials add thousands."
        },
        {
          question: "Does a new roof increase home value in NC?",
          answer: "Yes. Zillow shows ROI of 60–70% in NC markets."
        },
        {
          question: "How do I finance a new roof?",
          answer: "Carolina Trophy Roofs offers financing options for qualified homeowners."
        },
        {
          question: "Do new roofs lower insurance premiums?",
          answer: "Yes, especially with impact-resistant shingles."
        },
        {
          question: "Does a new roof lower energy bills?",
          answer: "Yes. Reflective shingles and metal reduce cooling costs."
        },
        {
          question: "Are roof repairs tax deductible?",
          answer: "No. But energy-efficient upgrades may qualify for credits."
        },
        {
          question: "How does roof ROI compare to other remodels?",
          answer: "Roofing ranks in the top 5 for resale ROI."
        },
        {
          question: "Do roofing materials affect insurance rates?",
          answer: "Yes. Impact-resistant products may lower rates."
        },
        {
          question: "Is financing available for emergency replacements?",
          answer: "Yes. We provide emergency financing options."
        },
        {
          question: "Can roof replacement be rolled into refinancing?",
          answer: "Yes. Many homeowners refinance to cover roof costs."
        },
        {
          question: "Do warranties affect resale value?",
          answer: "Yes. Our transferable warranties make homes more attractive."
        },
        {
          question: "How much more does metal cost vs shingles?",
          answer: "Metal costs more upfront but lasts 2–3 times longer."
        },
        {
          question: "Is preventive maintenance worth the cost?",
          answer: "Absolutely. Small repairs prevent major storm losses."
        }
      ]
    }
  ];

  // Clean, single FAQPage schema without nesting issues
  const faqPageSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "name": "Frequently Asked Questions - Roofing FAQ",
    "description": "Get expert answers to 100+ common roofing questions about installation, costs, insurance, warranties, and maintenance from Eastern NC's trusted roofing professionals.",
    "url": "https://www.carolinatrophyroofs.com/frequently-asked-questions",
    "mainEntity": faqCategories.flatMap(category => 
      category.questions.map(faq => ({
        "@type": "Question",
        "name": faq.question,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": faq.answer
        }
      }))
    ),
    "breadcrumb": {
      "@type": "BreadcrumbList",
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "name": "Home",
          "item": "https://www.carolinatrophyroofs.com"
        },
        {
          "@type": "ListItem", 
          "position": 2,
          "name": "FAQ",
          "item": "https://www.carolinatrophyroofs.com/frequently-asked-questions"
        }
      ]
    },
    "publisher": {
      "@type": "Organization",
      "name": "Carolina Trophy Roofs",
      "url": "https://www.carolinatrophyroofs.com",
      "telephone": "(252) 302-2003"
    }
  };

  return (
    <Layout>
      <Helmet>
        <title>100+ Roofing FAQs | Expert Answers | Carolina Trophy Roofs | Eastern NC</title>
        <meta name="description" content="Get expert answers to 100+ roofing questions about costs, insurance claims, materials, installation, and maintenance. Serving Eastern North Carolina homeowners." />
        <meta name="keywords" content="roofing FAQ, roof replacement questions, roofing costs NC, insurance claims, roof installation, storm damage, Eastern NC roofer" />
        <link rel="canonical" href="https://www.carolinatrophyroofs.com/frequently-asked-questions" />
        
        <meta property="og:title" content="100+ Roofing FAQs | Carolina Trophy Roofs" />
        <meta property="og:description" content="Expert answers to your roofing questions about installation, costs, insurance, and maintenance from Eastern NC's trusted professionals." />
        <meta property="og:url" content="https://www.carolinatrophyroofs.com/frequently-asked-questions" />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Carolina Trophy Roofs" />
        
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="100+ Roofing FAQs | Carolina Trophy Roofs" />
        <meta name="twitter:description" content="Expert answers to your roofing questions about installation, costs, insurance, and maintenance." />
        
        {/* Single, clean FAQPage schema */}
        <script type="application/ld+json">
          {JSON.stringify(faqPageSchema)}
        </script>
      </Helmet>

      {/* Hero Section */}
      <section className="relative bg-gradient-to-r from-brand-charcoal-gray to-gray-800 text-white py-20 md:py-28">
        <div className="absolute inset-0">
          <img 
            src="https://images.unsplash.com/photo-1521791136064-7986c2920216?w=1920&h=1080&fit=crop&auto=format&q=75" 
            alt="Roofing questions and answers" 
            className="w-full h-full object-cover opacity-30"
          />
        </div>
        <div className="relative container mx-auto px-4 z-10">
          <div className="max-w-4xl mx-auto text-center">
            <Badge className="mb-4 bg-brand-safety-orange text-white px-4 py-2 text-sm font-semibold">
              EXPERT ANSWERS
            </Badge>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 text-brand-trophy-gold">
              Frequently Asked Questions
            </h1>
            <p className="text-xl md:text-2xl mb-8 text-white max-w-3xl mx-auto">
              Get expert answers to common roofing questions from Eastern NC's trusted roofing professionals
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <Button size="lg" className="bg-brand-safety-orange hover:bg-brand-safety-orange/90 text-white font-semibold px-8 py-4">
                  <Calendar className="mr-2 h-5 w-5" />
                  Ask Our Experts
                </Button>
              </Link>
              <Button size="lg" variant="outline" className="border-brand-trophy-gold text-brand-trophy-gold hover:bg-brand-trophy-gold/10">
                <Phone className="mr-2 h-5 w-5" />
                Call (252) 302-2003
              </Button>
            </div>
          </div>
        </div>
      </section>

      <div className="container mx-auto py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          
          {/* Quick Links */}
          <div className="mb-16 text-center">
            <h2 className="text-2xl font-bold mb-8 text-brand-charcoal-gray">Quick Navigation</h2>
            <div className="flex flex-wrap justify-center gap-4">
              {faqCategories.map((category, index) => (
                <Badge 
                  key={index} 
                  variant="outline" 
                  className="px-4 py-2 text-sm border-brand-trophy-gold text-brand-charcoal-gray hover:bg-brand-trophy-gold/10 cursor-pointer"
                  onClick={() => document.getElementById(`category-${index}`)?.scrollIntoView({ behavior: 'smooth' })}
                >
                  <category.icon className="w-4 h-4 mr-2" />
                  {category.title}
                </Badge>
              ))}
            </div>
          </div>

          {/* FAQ Categories */}
          {faqCategories.map((category, categoryIndex) => (
            <div key={categoryIndex} id={`category-${categoryIndex}`} className="mb-12">
              <Card className="border-2 border-gray-200">
                <CardHeader className="bg-gradient-to-r from-gray-50 to-white">
                  <CardTitle className="flex items-center text-2xl font-bold text-brand-charcoal-gray">
                    <div className="w-10 h-10 bg-brand-trophy-gold/10 rounded-full flex items-center justify-center mr-4">
                      <category.icon className="h-6 w-6 text-brand-trophy-gold" />
                    </div>
                    {category.title}
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <Accordion type="single" collapsible className="w-full">
                    {category.questions.map((faq, faqIndex) => (
                      <AccordionItem key={faqIndex} value={`${categoryIndex}-${faqIndex}`} className="border-gray-200">
                        <AccordionTrigger className="px-6 py-4 text-left hover:bg-gray-50 font-semibold text-brand-charcoal-gray">
                          {faq.question}
                        </AccordionTrigger>
                        <AccordionContent className="px-6 pb-4 text-gray-700 leading-relaxed">
                          {faq.answer}
                        </AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                </CardContent>
              </Card>
            </div>
          ))}

          {/* Still Have Questions Section */}
          <Card className="bg-gradient-to-r from-brand-charcoal-gray to-gray-800 text-white mt-16">
            <CardContent className="p-8 md:p-12 text-center">
              <div className="w-16 h-16 bg-brand-trophy-gold/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <HelpCircle className="h-8 w-8 text-brand-trophy-gold" />
              </div>
              <h2 className="text-3xl font-bold mb-4 text-brand-trophy-gold">
                Still Have Questions?
              </h2>
              <p className="text-xl mb-8 max-w-2xl mx-auto text-gray-200">
                Our roofing experts are here to help. Get personalized answers to your specific roofing questions.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/contact">
                  <Button size="lg" className="bg-brand-safety-orange hover:bg-brand-safety-orange/90 text-white font-semibold px-8 py-4">
                    <Calendar className="mr-2 h-5 w-5" />
                    Schedule Consultation
                  </Button>
                </Link>
                <Button size="lg" variant="outline" className="border-brand-trophy-gold text-brand-trophy-gold hover:bg-brand-trophy-gold/10">
                  <Phone className="mr-2 h-5 w-5" />
                  Call (252) 302-2003
                </Button>
              </div>
              <div className="mt-8 text-sm text-gray-300">
                <p>✓ Free consultations • ✓ Expert advice • ✓ No pressure sales</p>
              </div>
            </CardContent>
          </Card>

          {/* Service Area Note */}
          <div className="mt-12 text-center">
            <div className="flex items-center justify-center mb-4">
              <MapPin className="h-5 w-5 text-brand-safety-orange mr-2" />
              <span className="font-semibold text-brand-charcoal-gray">Service Areas</span>
            </div>
            <p className="text-gray-600">
              Carolina Trophy Roofs proudly serves Rocky Mount, Greenville, Wilson, Tarboro, Zebulon, 
              and all of Eastern North Carolina with expert roofing services.
            </p>
          </div>

          {/* SEO Resources & Citations */}
          <Card className="mt-16 border-2 border-gray-200">
            <CardHeader className="bg-gradient-to-r from-gray-50 to-white">
              <CardTitle className="flex items-center text-2xl font-bold text-brand-charcoal-gray">
                <div className="w-10 h-10 bg-brand-trophy-gold/10 rounded-full flex items-center justify-center mr-4">
                  <FileText className="h-6 w-6 text-brand-trophy-gold" />
                </div>
                Industry Resources & References
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <p className="text-gray-600 mb-6">
                Our roofing expertise is backed by industry-leading organizations and authoritative sources. 
                These resources provide additional information to help you make informed decisions about your roofing needs.
              </p>
              
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-semibold text-brand-charcoal-gray mb-3">Industry Standards & Guidelines</h4>
                  <ul className="space-y-2 text-sm">
                    <li>
                      <a href="https://www.nrca.net/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 hover:underline">
                        National Roofing Contractors Association - Homeowner's Guide
                      </a>
                    </li>
                    <li>
                      <a href="https://www.asphaltroofing.org/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 hover:underline">
                        Asphalt Roofing Manufacturers Association - Maintenance Guide
                      </a>
                    </li>
                    <li>
                      <a href="https://www.consumerreports.org/cro/roofing/buying-guide/index.htm" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 hover:underline">
                        Consumer Reports - Roofing Warranties & Buying Guide
                      </a>
                    </li>
                    <li>
                      <a href="https://www.bbb.org/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 hover:underline">
                        Better Business Bureau - Contractor Standards
                      </a>
                    </li>
                  </ul>
                </div>
                
                <div>
                  <h4 className="font-semibold text-brand-charcoal-gray mb-3">Government & Educational Resources</h4>
                  <ul className="space-y-2 text-sm">
                    <li>
                      <a href="https://www.iii.org/article/how-much-homeowners-insurance-do-you-need" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 hover:underline">
                        Insurance Information Institute - Property Insurance Guide
                      </a>
                    </li>
                    <li>
                      <a href="https://www.fema.gov/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 hover:underline">
                        FEMA - Roof Damage Documentation Guidelines
                      </a>
                    </li>
                    <li>
                      <a href="https://www.energy.gov/energysaver/cool-roofs" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 hover:underline">
                        U.S. Department of Energy - Cool Roofs Guide
                      </a>
                    </li>
                    <li>
                      <a href="https://climate.ncsu.edu/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 hover:underline">
                        NC State University - Climate Impact Studies
                      </a>
                    </li>
                  </ul>
                </div>
              </div>
              
              <div className="mt-6">
                <h4 className="font-semibold text-brand-charcoal-gray mb-3">Specialized Services & Weather Preparedness</h4>
                <div className="grid md:grid-cols-2 gap-4">
                  <ul className="space-y-2 text-sm">
                    <li>
                      <a href="https://www.nachi.org/infrared-inspections.htm" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 hover:underline">
                        InterNACHI - Thermal Imaging Inspection Standards
                      </a>
                    </li>
                    <li>
                      <a href="https://www.weather.gov/safety/thunderstorm" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 hover:underline">
                        National Weather Service - Severe Weather Preparedness
                      </a>
                    </li>
                  </ul>
                  <ul className="space-y-2 text-sm">
                    <li>
                      <a href="https://www.epa.gov/heatisland/cool-roofs" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 hover:underline">
                        EPA - Building Efficiency & Cool Roofs
                      </a>
                    </li>
                    <li>
                      <a href="https://www.nationalroofingdirectory.org/united-states/rocky-mount/roofing-1/carolina-trophy-roofs" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 hover:underline">
                        National Roofing Directory - Carolina Trophy Roofs Recognition
                      </a>
                    </li>
                  </ul>
                </div>
              </div>
              
              <div className="mt-6 p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                <p className="text-sm text-gray-700">
                  <strong>Disclaimer:</strong> These external resources are provided for educational purposes. 
                  Carolina Trophy Roofs is not responsible for the content of external websites. 
                  Always consult with our certified professionals for specific roofing advice tailored to your situation.
                </p>
              </div>
            </CardContent>
          </Card>

        </div>
      </div>
    </Layout>
  );
};

export default FAQPage;